import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Navbar, Dropdown, Footer } from 'flowbite-react';
import { Gear, SignOut, Sun, Moon, House, Book, Question, Compass, DotsThree, StarAndCrescent, MessageCircle } from '@phosphor-icons/react';
import { SalahIcon, Mosque01Icon, Kaaba01Icon, MoreHorizontalCircle01Icon, RubElHizbIcon, Quran01Icon, Ramadhan01Icon, Settings02Icon, Message01Icon, Logout02Icon, Sun03Icon, Moon02Icon, Sun01Icon } from "hugeicons-react";
import CustomAvatar from './CustomAvatar';
import { useDarkMode } from '../contexts/DarkModeContext';
import { motion, AnimatePresence } from 'framer-motion';
import NonLoggedInOverlay from './NonLoggedInOverlay';
import { isAndroid } from '../utils/platformDetection';

const Layout = () => {
  const { currentUser, logout } = useAuth();
  const { settings } = useSettings();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  const isSimpleDashboard = location.pathname === '/simpledashboard';
  const isQuranPage = location.pathname === '/quran';

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  console.log('Current user:', {
    photoURL: currentUser?.photoURL,
    displayName: currentUser?.displayName,
    email: currentUser?.email
  });

  useEffect(() => {
    if (currentUser) {
      console.log('Current user data:', {
        photoURL: currentUser.photoURL,
        providerId: currentUser.providerData[0]?.providerId,
        providerPhotoURL: currentUser.providerData[0]?.photoURL
      });
    }
  }, [currentUser]);

  // Add this function to determine device type
  const getDeviceType = () => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  };

  const [deviceType, setDeviceType] = useState(getDeviceType());

  useEffect(() => {
    const handleResize = () => {
      setDeviceType(getDeviceType());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine if the special desktop dashboard background should be shown
  const isDesktopDashboard = location.pathname === '/' && deviceType !== 'mobile';
  
  // Add debug log to check the values
  useEffect(() => {
    console.log('Layout debug:', {
      pathname: location.pathname,
      deviceType,
      isDesktopDashboard,
      isDarkMode,
      backgroundApplied: isDesktopDashboard ? 'widebgpattern.png with proper styling' : 'default background'
    });
  }, [location.pathname, deviceType, isDesktopDashboard, isDarkMode]);

  // Add this component before the MobileNavigation component
  const MobileDropup = ({ isOpen, onClose, currentUser, logout, isDarkMode, toggleDarkMode }) => {
    const getUserInitials = () => {
      if (currentUser && currentUser.displayName) {
        return currentUser.displayName.split(' ').map(n => n[0]).join('').toUpperCase();
      } else if (currentUser && currentUser.email) {
        return currentUser.email[0].toUpperCase();
      }
      return '?';
    };

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 pointer-events-auto"
            onClick={onClose}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ 
                type: 'spring', 
                damping: 25, 
                stiffness: 200,
                exit: { type: 'tween', duration: 0.25, ease: 'easeIn' }
              }}
              className={`absolute bottom-[65px] left-0 right-0 rounded-t-3xl overflow-hidden ${
                isDarkMode ? 'bg-[#111111]' : 'bg-white'
              }`}
              onClick={e => e.stopPropagation()}
            >
              <div className={`p-6 border-b ${isDarkMode ? 'border-white/[0.05]' : 'border-black/[0.02]'}`}>
                <div className="flex items-center gap-4">
                  <CustomAvatar 
                    src={currentUser ? currentUser.photoURL : undefined}
                    alt={currentUser ? (currentUser.displayName || "User") : "Guest"}
                    initials={currentUser ? getUserInitials() : "G"}
                    size={48}
                  />
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}> 
                      {currentUser ? (currentUser.displayName || currentUser.email) : "Guest User"}
                    </div>
                    <div className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}> 
                      {currentUser ? currentUser.email : "Not signed in"}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Menu items */}
              <div className="p-2 pb-4">
                {/* Settings */}
                <Link
                  to="/settings"
                  onClick={onClose}
                  className={`flex items-center gap-3 w-full p-4 rounded-xl transition-colors ${
                    isDarkMode 
                      ? 'text-gray-300 hover:bg-white/[0.05] active:bg-white/[0.08]' 
                      : 'text-gray-700 hover:bg-black/[0.02] active:bg-black/[0.05]'
                  }`}
                >
                  <Settings02Icon size={20} weight="regular" className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                  <span className="font-medium">Settings</span>
                </Link>

                {/* Feedback */}
                <Link
                  to="/feedback"
                  onClick={onClose}
                  className={`flex items-center gap-3 w-full p-4 rounded-xl transition-colors ${
                    isDarkMode 
                      ? 'text-gray-300 hover:bg-white/[0.05] active:bg-white/[0.08]' 
                      : 'text-gray-700 hover:bg-black/[0.02] active:bg-black/[0.05]'
                  }`}
                >
                  <Message01Icon size={20} weight="regular" className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                  <span className="font-medium">Send Feedback</span>
                </Link>

                {/* Theme Toggle */}
                <button
                  onClick={() => {
                    toggleDarkMode();
                    onClose();
                  }}
                  className={`flex items-center gap-3 w-full p-4 rounded-xl transition-colors ${
                    isDarkMode 
                      ? 'text-gray-300 hover:bg-white/[0.05] active:bg-white/[0.08]' 
                      : 'text-gray-700 hover:bg-black/[0.02] active:bg-black/[0.05]'
                  }`}
                >
                  {isDarkMode ? (
                    <>
                      <Sun03Icon size={20} weight="regular" className="text-emerald-400" />
                      <span className="font-medium">Light Mode</span>
                    </>
                  ) : (
                    <>
                      <Moon02Icon size={20} weight="regular" className="text-gray-500" />
                      <span className="font-medium">Dark Mode</span>
                    </>
                  )}
                </button>

                {/* Sign Out or Sign In */}
                {currentUser ? (
                  <button
                    onClick={() => {
                      onClose();
                      logout();
                    }}
                    className={`flex items-center gap-3 w-full p-4 rounded-xl transition-colors mt-2 ${
                      isDarkMode 
                        ? 'text-red-400 hover:bg-red-500/10 active:bg-red-500/20' 
                        : 'text-red-600 hover:bg-red-50 active:bg-red-100'
                    }`}
                  >
                    <Logout02Icon size={20} weight="regular" className="current-color" />
                    <span className="font-medium">Sign out</span>
                  </button>
                ) : (
                  <Link
                    to={window.isUmmahtiNative ? '/' : '/login'}
                    onClick={onClose}
                    state={window.isUmmahtiNative ? { from: 'signin' } : null}
                    className={`flex items-center gap-3 w-full p-4 rounded-xl transition-colors mt-2 ${
                      isDarkMode 
                        ? 'text-emerald-400 hover:bg-emerald-500/10 active:bg-emerald-500/20' 
                        : 'text-emerald-600 hover:bg-emerald-50 active:bg-emerald-100'
                    }`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    <span className="font-medium">Sign in</span>
                  </Link>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  // Modify the MobileNavigation component
  const MobileNavigation = () => {
    const [isDropupOpen, setIsDropupOpen] = useState(false);
    const location = useLocation();

    const isActive = (path) => location.pathname === path;

    const NavItem = ({ to, icon, label }) => (
      <Link 
        to={to} 
        className="flex flex-col items-center py-2 px-3 space-y-1"
      >
        <div className={`${
          isActive(to) 
            ? isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
            : 'text-gray-400'
        } transition-colors hover:text-emerald-600`}>
          {icon}
        </div>
        <span className={`text-xs ${
          isActive(to) 
            ? isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
            : 'text-gray-400'
        } transition-colors group-hover:text-emerald-600`}>
          {label}
        </span>
      </Link>
    );

    return (
      <>
        {/* Gradient overlay - different for dark/light mode */}
        <div className={`fixed bottom-0 left-0 right-0 h-28 bg-gradient-to-t pointer-events-none z-[90] ${
          isDarkMode
            ? 'from-emerald-800/30 via-emerald-900/20 to-transparent'
            : 'from-gray-200/30 via-gray-100/10 to-transparent'
        }`} />
        
        {/* Navigation bar with glass effect */}
        <div className="fixed left-0 right-0 bottom-0 z-[100]">
          <nav
            className={`backdrop-blur-xl px-6 py-2 safe-area-bottom ${
              isDarkMode
                ? 'bg-black/25 border-t border-white/10'
                : 'bg-white/90 border-t border-gray-200/50'
            }`}
            style={
              isAndroid()
                ? { paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }
                : undefined
            }
          >
            <div className="max-w-md mx-auto flex justify-between items-center">
              <NavItem 
                to="/dashboard" 
                icon={<Mosque01Icon size={24} />} 
                label="Home" 
              />
              <NavItem 
                to="/quran" 
                icon={<Quran01Icon size={24} />} 
                label="Quran" 
              />
              <NavItem 
                to="/deen-helpers" 
                icon={<SalahIcon size={24} />} 
                label="Deen"
              />
              <NavItem 
                to="/qibla-finder" 
                icon={<Kaaba01Icon size={24} />} 
                label="Qibla" 
              />
              <button 
                onClick={() => setIsDropupOpen(prev => !prev)}
                className="flex flex-col items-center py-2 px-3 space-y-1"
              >
                <div className={`${
                  isDropupOpen 
                    ? isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                    : 'text-gray-400'
                } transition-colors hover:text-emerald-600`}>
                  <MoreHorizontalCircle01Icon size={24} />
                </div>
                <span className={`text-xs ${
                  isDropupOpen 
                    ? isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                    : 'text-gray-400'
                } transition-colors group-hover:text-emerald-600`}>
                  More
                </span>
              </button>
            </div>
          </nav>
        </div>
        
        <MobileDropup
          isOpen={isDropupOpen}
          onClose={() => setIsDropupOpen(false)}
          currentUser={currentUser}
          logout={logout}
          isDarkMode={isDarkMode}
          toggleDarkMode={toggleDarkMode}
        />
      </>
    );
  };

  if (isSimpleDashboard) {
    return <Outlet />;
  }

  const getUserInitials = () => {
    if (currentUser.displayName) {
      return currentUser.displayName.split(' ').map(n => n[0]).join('').toUpperCase();
    } else if (currentUser.email) {
      return currentUser.email[0].toUpperCase();
    }
    return '?';
  };

  const getFirstAndLastName = () => {
    if (currentUser.displayName) {
      const names = currentUser.displayName.split(' ');
      return {
        firstName: names[0],
        lastName: names.slice(1).join(' ')
      };
    }
    return {
      firstName: currentUser.email?.split('@')[0] || '',
      lastName: ''
    };
  };

  // Modify the main return statement
  return (
    <div 
      className={`relative flex flex-col min-h-[100dvh] has-safe-area transition-colors duration-200 ${
        isDesktopDashboard ? 'bg-transparent' : isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
      }`}
    >
      {/* Non-logged-in overlay */}
      <NonLoggedInOverlay show={!currentUser} />

      {/* Conditionally render Desktop Dashboard Background */} 
      {isDesktopDashboard && (
        <div className="fixed inset-0 overflow-hidden pointer-events-none"> {/* Fixed positioning */}
          {/* Base gradient */}
          <div className={`absolute inset-0 ${
            isDarkMode
              ? 'bg-gradient-to-b from-[#111111] via-[#111111] to-[#111111]' // Dark gradient
              : 'bg-gradient-to-b from-gray-100 to-gray-50' // Light gradient
          }`} />
          
          {/* Background pattern - Using consistent approach as in PrivacyPolicy.js and MobileLayout.js */}
          {isDarkMode ? (
            // Dark mode pattern
            <div className="absolute inset-0"
                 style={{
                   backgroundImage: 'url("/images/widebgpattern.png")',
                   backgroundSize: 'cover',
                   backgroundPosition: 'center center',
                   opacity: 0.07,
                   mixBlendMode: 'lighten'
                 }}>
            </div>
          ) : (
            // Light mode pattern with inversion
            <div className="absolute inset-0"
                 style={{
                   backgroundImage: 'url("/images/widebgpattern.png")',
                   backgroundSize: 'cover',
                   backgroundPosition: 'center center',
                   opacity: 0.04,
                   filter: 'invert(1)',
                   mixBlendMode: 'multiply'
                 }}>
            </div>
          )}
          
          {/* Subtle gradient overlay */}
          <div className={`absolute inset-0 ${
            isDarkMode
              ? 'bg-gradient-to-b from-[#042f21]/30 via-transparent to-transparent' // Darker green hint at top only
              : 'bg-gradient-to-br from-emerald-50/70 via-transparent to-transparent' // Lighter green hint 
          }`} />
          
          {/* Simplified orbs - fewer, more subtle, and positioned better */}
          <div className={`absolute top-1/3 left-1/4 w-[50rem] h-[50rem] rounded-full blur-[128px] opacity-10 ${
            isDarkMode ? 'bg-emerald-700' : 'bg-emerald-600'
          }`} />
          <div className={`absolute bottom-0 right-1/3 w-[40rem] h-[40rem] rounded-full blur-[128px] opacity-10 ${
            isDarkMode ? 'bg-emerald-900' : 'bg-emerald-400'
          }`} />
          
          {/* Add subtle vignette effect */}
          <div className={`absolute inset-0 ${
            isDarkMode
              ? 'bg-radial-gradient-to-center from-transparent to-black/20'
              : 'bg-radial-gradient-to-center from-transparent to-black/5'
          }`} />
          
          {/* Additional light mode styling */}
          {!isDarkMode && (
            <>
              <div className="absolute inset-0 bg-gradient-to-b from-gray-100/90 via-transparent to-gray-100/90" />
              <div className="absolute inset-0 backdrop-blur-[15px]" />
            </>
          )}
        </div>
      )}

      {/* Only show navbar on tablet and desktop */}
      {deviceType !== 'mobile' && (
        <Navbar 
          fluid 
          className={`sticky top-0 z-50 transition-all duration-300 ${
            isQuranPage 
              ? 'bg-white dark:bg-[#0A0A0A] border-b border-gray-200 dark:border-[#0A0A0A]' : 
            scrolled 
              ? 'bg-white/95 dark:bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-gray-200 dark:border-[#0A0A0A]' : 
            'bg-transparent border-transparent'
          }`}
        >
          <div className={isQuranPage ? "w-full px-6" : "container mx-auto px-6"}>
            <div className="w-full flex justify-between items-center py-4">
              <div className="flex items-center gap-8">
                <Navbar.Brand 
                  as={Link} 
                  to="/" 
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full transition-all duration-300 ${
                    scrolled 
                      ? 'hover:bg-gray-100 dark:hover:bg-[#111111]' 
                      : isDarkMode 
                        ? 'bg-[#111111] hover:bg-[#1A1A1A] shadow-sm' 
                        : 'bg-white hover:bg-gray-50 shadow-sm'
                  }`}
                >
                  <img
                    src={isDarkMode ? "/images/logo_white.png" : "/images/logo_green.png"}
                    className="h-8 transition-opacity duration-300"
                    alt="Islamic Dashboard Logo"
                  />
                </Navbar.Brand>
                
                {currentUser && (
                  <div className={`hidden md:flex items-center gap-3 px-4 py-2 rounded-full transition-all duration-300 ${
                    scrolled 
                      ? isDarkMode ? 'bg-[#111111]/70 shadow-sm backdrop-blur-sm' : 'bg-white/70 shadow-sm backdrop-blur-sm' 
                      : isDarkMode ? 'bg-[#111111] shadow-sm' : 'bg-white shadow-sm'
                  }`}>
                    <Link 
                      to="/" 
                      className={`px-5 py-2.5 rounded-full transition-all duration-300 ${
                        location.pathname === '/' 
                          ? isDarkMode ? 'bg-emerald-500 text-black font-medium shadow-lg shadow-emerald-500/20' : 'bg-gray-900 text-white'
                          : isDarkMode ? 'text-gray-400 hover:text-white hover:bg-[#1A1A1A]' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Dashboard
                    </Link>
                    <Link 
                      to="/quran" 
                      className={`px-5 py-2.5 rounded-full transition-all duration-300 ${
                        location.pathname === '/quran' 
                          ? isDarkMode ? 'bg-emerald-500 text-black font-medium shadow-lg shadow-emerald-500/20' : 'bg-gray-900 text-white'
                          : isDarkMode ? 'text-gray-400 hover:text-white hover:bg-[#1A1A1A]' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Quran
                    </Link>
                    <Link 
                      to="/deen-helpers" 
                      className={`px-5 py-2.5 rounded-full transition-all duration-300 ${
                        location.pathname === '/deen-helpers' 
                          ? isDarkMode ? 'bg-emerald-500 text-black font-medium shadow-lg shadow-emerald-500/20' : 'bg-gray-900 text-white'
                          : isDarkMode ? 'text-gray-400 hover:text-white hover:bg-[#1A1A1A]' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Deen Helpers
                    </Link>
                    <Link 
                      to="/qibla-finder" 
                      className={`lg:hidden px-5 py-2.5 rounded-full transition-all duration-300 ${
                        location.pathname === '/qibla-finder' 
                          ? isDarkMode ? 'bg-emerald-500 text-black font-medium shadow-lg shadow-emerald-500/20' : 'bg-gray-900 text-white'
                          : isDarkMode ? 'text-gray-400 hover:text-white hover:bg-[#1A1A1A]' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Qibla Finder
                    </Link>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                {currentUser ? (
                  <Dropdown
                    arrowIcon={false}
                    inline
                    label={
                      <div className={`flex items-center gap-4 cursor-pointer rounded-full transition-all duration-300 h-11 ${
                        scrolled 
                          ? 'hover:bg-gray-100 dark:hover:bg-[#111111]' 
                          : isDarkMode 
                            ? 'bg-[#111111] hover:bg-[#1A1A1A] shadow-sm' 
                            : 'bg-white hover:bg-gray-50 shadow-sm'
                      }`}>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleDarkMode();
                          }}
                          className={`h-full px-4 rounded-full transition-colors flex items-center justify-center ${
                            isDarkMode 
                              ? 'hover:bg-[#1A1A1A]' 
                              : 'hover:bg-gray-100'
                          }`}
                        >
                          {isDarkMode ? (
                            <Sun01Icon size={20} className="text-emerald-400 hover:text-emerald-300 transition-colors" />
                          ) : (
                            <Moon02Icon size={20} className="text-gray-700 hover:text-gray-900 transition-colors" />
                          )}
                        </button>
                        <div className="pr-4 flex items-center">
                          <CustomAvatar 
                            src={currentUser.photoURL}
                            alt={currentUser.displayName || "User settings"}
                            initials={getUserInitials()}
                          />
                        </div>
                      </div>
                    }
                    className="!p-0 [&>div]:!p-0 [&>div]:!bg-transparent [&>div]:!border-0 [&>div]:!rounded-none [&>div]:!m-0 [&>div>ul]:!py-0"
                  >
                    <div className={`rounded-[24px] overflow-hidden border transition-all duration-200 min-w-[240px] ${
                      isDarkMode 
                        ? 'bg-[#111111] border-gray-800 shadow-lg shadow-black/5' 
                        : 'bg-white border-gray-100 shadow-sm'
                    }`}>
                      <Dropdown.Header className={`p-4 border-b ${isDarkMode ? 'bg-[#111111] text-white border-gray-800' : 'border-gray-100'}`}>
                        <span className="block text-sm">{currentUser.displayName || currentUser.email}</span>
                        <span className={`block truncate text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {currentUser.email}
                        </span>
                      </Dropdown.Header>
                      <ul className="p-2">
                        <li>
                          <Dropdown.Item 
                            as={Link} 
                            to="/settings"
                            className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                              isDarkMode 
                                ? 'text-gray-300 hover:bg-[#1A1A1A]' 
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <Settings02Icon size={18} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                            Settings
                          </Dropdown.Item>
                        </li>
                        <li>
                          <Dropdown.Item 
                            onClick={logout}
                            className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                              isDarkMode 
                                ? 'text-red-400 hover:bg-red-500/10' 
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                          >
                            <Logout02Icon size={18} className="current-color" />
                            Sign out
                          </Dropdown.Item>
                        </li>
                      </ul>
                    </div>
                  </Dropdown>
                ) : (
                  <div className={`flex items-center gap-3 px-4 py-2 rounded-full transition-all duration-300 relative ${
                    scrolled 
                      ? 'hover:bg-gray-100 dark:hover:bg-[#111111]' 
                      : isDarkMode 
                        ? 'bg-[#111111] hover:bg-[#1A1A1A] shadow-sm' 
                        : 'bg-white hover:bg-gray-50 shadow-sm'
                  }`}>
                    <div 
                      className={`absolute inset-2 transition-all duration-500 ease-in-out rounded-full ${
                        isDarkMode 
                          ? 'bg-emerald-500 shadow-lg shadow-emerald-500/20' 
                          : 'bg-gray-900'
                      }`}
                      style={{
                        left: location.pathname === '/login' ? '8px' : '50%',
                        right: location.pathname === '/signup' ? '8px' : '50%',
                        opacity: (location.pathname === '/login' || location.pathname === '/signup') ? 1 : 0,
                      }}
                    />
                    
                    <Link 
                      to="/login" 
                      className={`px-5 py-2.5 rounded-full transition-all duration-300 relative z-10 ${
                        location.pathname === '/login' 
                          ? isDarkMode 
                            ? 'text-black font-medium' 
                            : 'text-white font-medium'
                          : isDarkMode 
                            ? 'text-gray-400 hover:text-white hover:bg-[#1A1A1A]' 
                            : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Login
                    </Link>
                    <Link 
                      to="/signup" 
                      className={`px-5 py-2.5 rounded-full transition-all duration-300 relative z-10 ${
                        location.pathname === '/signup' 
                          ? isDarkMode 
                            ? 'text-black font-medium' 
                            : 'text-white font-medium'
                          : isDarkMode 
                            ? 'text-gray-400 hover:text-white hover:bg-[#1A1A1A]' 
                            : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Sign Up
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Navbar>
      )}

      {/* Main content wrapper - Added relative z-10 */} 
      <main className={`relative z-10 flex-1 ${deviceType === 'mobile' ? 'mb-16' : ''}`}>  
        <div
          style={isAndroid() ? { paddingTop: 'env(safe-area-inset-top, 16px)' } : undefined}
          className="h-full"
        >
          <div className="h-full bg-transparent"> {/* Added bg-transparent to ensure background shows through */}
            <Outlet />
          </div>
        </div>
      </main>

      {/* Only show mobile navigation when onboarding is complete or for guests */}
      {deviceType === 'mobile' && (!currentUser || settings?.onboardingCompleted) && <MobileNavigation />}

      {/* Footer - Added relative z-10 */} 
      {deviceType !== 'mobile' && (
        <Footer container={false} className={`relative z-10 mt-auto transition-all duration-200 !rounded-none ${ 
          isDarkMode 
            ? 'bg-[#111111] shadow-lg shadow-black/5' 
            : 'bg-white shadow-sm'
        }`}>
          <div className="w-full text-center">
            <div className="container px-9 pt-6 mx-auto justify-between sm:flex sm:items-center sm:justify-between">
              <Footer.Copyright
                by="Ummahti"
                year={new Date().getFullYear()}
                className={isDarkMode ? 'text-gray-400' : ''}
              />
              <Footer.LinkGroup>
                <Footer.Link 
                  href="/about"
                  className={isDarkMode ? 'text-gray-400 hover:text-white' : ''}
                >
                  About
                </Footer.Link>
                <Footer.Link 
                  href="/privacy"
                  className={isDarkMode ? 'text-gray-400 hover:text-white' : ''}
                >
                  Privacy Policy
                </Footer.Link>
                <Footer.Link 
                  href="/contact"
                  className={isDarkMode ? 'text-gray-400 hover:text-white' : ''}
                >
                  Contact
                </Footer.Link>
              </Footer.LinkGroup>
            </div>
          </div>
        </Footer>
      )}
    </div>
  );
};

export default Layout;
